import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import * as React from "react";

import styles from "./ToolRequestDisclosure.module.css";

interface ToolRequestDisclosureProps {
  /** The proposed tool input, already stripped of the server's meta-fields. */
  params: Record<string, unknown>;
}

/**
 * The tool's proposed arguments, collapsed behind a "Request" toggle.
 *
 * The approval card renders this in place of a rich preview when
 * `confirmationContentFor` returns nothing: the tool declares no
 * `ConfirmationContent`, or its `validateInput` rejected these arguments. A
 * rejected proposal is the case where the arguments are the only statement of
 * what Approve authorizes, so they stay reachable — collapsed, because for
 * most tools the whole request is one route name.
 *
 * Renders nothing for a request with no arguments, leaving the card's
 * "wants to run" header to stand alone.
 */
export function ToolRequestDisclosure({ params }: ToolRequestDisclosureProps) {
  const [expanded, setExpanded] = React.useState(false);

  if (Object.keys(params).length === 0) {
    return null;
  }

  return (
    <div className={styles.disclosure}>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
      >
        {expanded ? (
          <ExpandMoreIcon className={styles.chevronIcon} />
        ) : (
          <ChevronRightIcon className={styles.chevronIcon} />
        )}
        Request
      </button>

      {expanded && (
        <pre className={styles.json}>{JSON.stringify(params, null, 2)}</pre>
      )}
    </div>
  );
}
